import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState, useMemo } from "react";
import {
  getIngrediants,
  getIngrediantsStatus,
  selectAllIngrediants,
} from "../../../features/ingrediantSlice";
import {
  getProductsStatus,
  getSingleProducts,
  selectAllProducts,
} from "../../../features/productSlice";
import { useDispatch, useSelector } from "react-redux";
import TextFieldCompnent from "../../../components/textFieldComponent";
import RadioButtonComponent from "../../../components/radioButtonComponent";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import ButtonComponent from "../../../components/ButtonComponent";
import SelectListUI from "../../../components/selectListUI";
import { OptionMode } from "../../../models/option.model";
import FullScreenDialog from "../../../components/FullScreenDialog";
import AddProduct from "../../categories/product/addProduct";
import AddIngrediant from "../../addons/ingrediants/addIngrediant";
import Reorder from "../../../components/reorder";
import { useTranslation } from "react-i18next";

const OptionForm = ({
  buttonText,
  handleFormSubmit,
  initialValues,
  loading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { restaurantId } = useRestaurant();
  const [productsLoading, setProductLoading] = useState(true);
  const [ingreadiantLoading, setIngreadiantLoading] = useState(true);
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const productStatus = useSelector(getProductsStatus);
  const ingrediantStatus = useSelector(getIngrediantsStatus);
  const ingredients = useSelector(selectAllIngrediants);
  const products = useSelector(selectAllProducts);
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [openAddIngredient, setOpenAddIngredient] = useState(false);
  const [localIngredients, setLocalIngredients] = useState([]);
  const [localProducts, setLocalProducts] = useState([]);

  const typeSchema = yup.object().shape({
    name: yup.string().required("Nom est requis"),
    label: yup.string().required("Libellé est requis"),
    message: yup.string(),
    max: yup
      .number()
      .required("Quantité maximal d'ingrédiant est requis")
      .min(1, "La quantité minimal est 1"),
    min: yup
      .number()
      .required("Quantité minimal d'ingrédiant est requis")
      .min(0, "La quantité minimal est 0")
      .test(
        "is-less-than-or-equal",
        "La quantité minimale doit être inférieure ou égale à la quantité maximale",
        function (value) {
          const { max } = this.parent;
          return value <= max;
        }
      ),
    payment: yup.boolean(),
    selection: yup.boolean(),
    mode: yup.string().required("Mode est requis"),
    ingredients: yup
      .array(),
    products: yup
      .array()
  });

  useEffect(() => {
    dispatch(getIngrediants(restaurantId));
    dispatch(getSingleProducts(restaurantId));
  }, [dispatch, restaurantId]);

  useEffect(() => {
    setLocalIngredients(ingredients);
  }, [ingredients]);

  useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  useEffect(() => {
    if (productStatus === "fetchSingleProducts") {
      setProductLoading(false);
    }
    if (ingrediantStatus === "loadingFetchData") {
      setIngreadiantLoading(false);
    }
  }, [ingrediantStatus, productStatus]);

  const ingredientsMap = useMemo(() => {
    const map = {};
    localIngredients.forEach((i) => (map[i._id] = i));
    return map;
  }, [localIngredients]);

  const productsMap = useMemo(() => {
    const map = {};
    localProducts.forEach((p) => (map[p._id] = p));
    return map;
  }, [localProducts]);
  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={typeSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => {
        const selectedIngredients = (values.ingredients || [])
          .map((id) => ingredientsMap[id])
          .filter(Boolean);
        const selectedProducts = (values.products || [])
          .map((id) => productsMap[id])
          .filter(Boolean);

        const setIngredientsFromFullList = (fullList) =>
          setFieldValue(
            "ingredients",
            fullList.map((it) => it._id)
          );

        const setProductsFromFullList = (fullList) =>
          setFieldValue(
            "products",
            fullList.map((it) => it._id)
          );

        const handleCloseAddProduct = (createdProduct) => {
          setOpenAddProduct(false);
          dispatch(getSingleProducts(restaurantId));
          const newProd = createdProduct?.product;
          if (newProd) {
            setFieldValue("products", [...values.products, newProd._id]);
            setLocalProducts((prev) => [...prev, newProd]);
          }
        };

        const handleCloseAddIngredient = (createdIng) => {
          setOpenAddIngredient(false);
          dispatch(getIngrediants(restaurantId));

          const newIng = createdIng?.ingredient;

          if (newIng) {
            setFieldValue("ingredients", [...values.ingredients, newIng._id]);
            setLocalIngredients((prev) => [...prev, newIng]);
          }
        };
        const handleIngredientsReorder = (newFullList) => {
          setIngredientsFromFullList(newFullList);
        };

        const handleProductsReorder = (newFullList) => {
          setProductsFromFullList(newFullList);
        };

        return (
          <>
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="16px"
                gridTemplateColumns="repeat(6, minmax(0, 1fr))"
                sx={{
                  "& > div": { gridColumn: isNonMobile ? undefined : "span 6" },
                }}
              >
                <TextFieldCompnent
                  type="text"
                  label={t("fields.name")}
                  change={handleChange}
                  value={values.name}
                  name="name"
                  blur={handleBlur}
                  touched={touched.name}
                  error={errors.name}
                  colum="span 3"
                  row="1 / span 1"
                  required
                />
                <TextFieldCompnent
                  type="text"
                  label={t("fields.label")}
                  change={handleChange}
                  value={values.label}
                  name="label"
                  blur={handleBlur}
                  touched={touched.label}
                  error={errors.label}
                  colum="span 3"
                  row="2 / span 1"
                  required
                />
                <TextFieldCompnent
                  type="text"
                  label={t("fields.description")}
                  change={handleChange}
                  value={values.message}
                  name="message"
                  blur={handleBlur}
                  touched={touched.message}
                  error={errors.message}
                  colum="span 3"
                  row="3 / span 1"
                />
                <TextFieldCompnent
                  label={t("fields.minQty")}
                  type="number"
                  change={handleChange}
                  value={values.min}
                  name="min"
                  blur={handleBlur}
                  touched={touched.min}
                  error={errors.min}
                  colum="span 3"
                  row="4 / span 1"
                  num={0}
                  onlyDigits={true}
                  required
                />
                <TextFieldCompnent
                  label={t("fields.maxQty")}
                  type="number"
                  change={handleChange}
                  value={values.max}
                  name="max"
                  blur={handleBlur}
                  touched={touched.max}
                  error={errors.max}
                  colum="span 3"
                  row="5 / span 1"
                  num={1}
                  onlyDigits={true}
                  required
                />
                <RadioButtonComponent
                  change={handleChange}
                  colum="span 3"
                  row="6 / span 1"
                  name="payment"
                  radioText1={t("common.no")}
                  radioText2={t("common.yes")}
                  radioValue1={false}
                  radioValue2={true}
                  text={t("fields.allIngredientsPaid")}
                  value={values.payment}
                />
                <RadioButtonComponent
                  change={handleChange}
                  colum="span 3"
                  row="7 / span 1"
                  name="selection"
                  radioText1={t("fields.single")}
                  radioText2={t("filters.multiple")}
                  radioValue1={false}
                  radioValue2={true}
                  text={t("fields.selectionType")}
                  value={values.selection}
                />
                <RadioButtonComponent
                  change={handleChange}
                  colum="span 3"
                  row="8 / span 1"
                  name="mode"
                  radioText1={t("filters.ingredients")}
                  radioText2={t("filters.products")}
                  radioValue1={OptionMode.ingredients}
                  radioValue2={OptionMode.products}
                  text={t("fields.mode")}
                  value={values.mode}
                  required
                />

                {values.mode === OptionMode.ingredients && (
                  <Box sx={{ gridColumn: "span 6" }}>
                    <Reorder
                      items={selectedIngredients}
                      onReorder={handleIngredientsReorder}
                      action={
                        <>
                          <SelectListUI
                            name="ingredients"
                            title={t("fields.selectIngredients")}
                            items={ingredients}
                            value={values.ingredients}
                            change={(e) => handleChange(e)}
                            error={errors.ingredients}
                            touched={touched.ingredients}
                            loading={ingreadiantLoading}
                            buttonOnly={true}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenAddIngredient(true)}
                          >
                            {t("actions.addIngredient")}
                          </Button>
                        </>
                      }
                    />
                  </Box>
                )}
                {values.mode === OptionMode.products && (
                  <Box sx={{ gridColumn: "span 6" }}>
                    <Reorder
                      items={selectedProducts}
                      onReorder={handleProductsReorder}
                      action={
                        <>
                          <SelectListUI
                            name="products"
                            title={t("fields.selectProducts")}
                            items={products}
                            value={values.products}
                            change={(e) => handleChange(e)}
                            error={errors.products}
                            touched={touched.products}
                            loading={productsLoading}
                            buttonOnly={true}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenAddProduct(true)}
                          >
                            {t("actions.addProduct")}
                          </Button>
                        </>
                      }
                    />
                  </Box>
                )}
              </Box>

              <ButtonComponent text={buttonText} loading={loading} />
            </form>

            <FullScreenDialog
              open={openAddProduct}
              onClose={handleCloseAddProduct}
              title={t("headers.addProductSub")}
            >
              <AddProduct onClose={handleCloseAddProduct} isDialog />
            </FullScreenDialog>

            <FullScreenDialog
              open={openAddIngredient}
              onClose={handleCloseAddIngredient}
              title={t("headers.addIngredientSub")}
            >
              <AddIngrediant onClose={handleCloseAddIngredient} isDialog />
            </FullScreenDialog>
          </>
        );
      }}
    </Formik>
  );
};

export default OptionForm;
